import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, ScrollView, StyleSheet } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import { scale, width } from '@components/frame';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';

import { ncFileStatisticsTimes, saveNCFileStatisticsTimes } from '@reducers/statistics';

import { StatusType, ChangeFunctionValue, ContextType, LevelType, TimesProps, NCFileStatisticsTimesType, StoreType } from './interfaces';

import Block from './components/Block';
import Loading from '@components/loading';
import { isPhone, Alert, getNcFileTimesConfig } from '@helpers';
import { secondsToInputDisplay } from '@v2/helpers/time';
import Selectors from '@styles/selectors';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { BULLET } from '@v2/pages/common/constants/base.constants';

let saveTimeOut: null | NodeJS.Timeout = null;

const Times = (props: TimesProps) => {
  const {
    C,
    ncFileStatisticsTimes,
    saveNCFileStatisticsTimes,
    ncFileId,
    containerWidth,
    withoutPaddingHorizontal,
    style,
    horizontalOffset,
    horizontalOffsetForShadow = 2,
  } = props;

  const [activeMobileTabTime, setActiveMobileTabTime] = useState<StatusType>('Setup');

  useEffect(() => {
    (async () => {
      if (ncFileId) {
        const data = (await ncFileStatisticsTimes({
          ncFileId,
        })) as NCFileStatisticsTimesType;

        const ncFileTimesConfig = getNcFileTimesConfig({ data, secondsToInputDisplay });

        setStore(ncFileTimesConfig);
      }

      setLoading(false);
    })();
  }, [ncFileId]);

  const [loading, setLoading] = useState(true);

  const [store, setStore] = useState<StoreType>({
    setup: {
      ideal: null,
      scheduled: null,
      manualIdeal: true,
      manualScheduled: true,
      idealAutomatic: '',
      scheduledAutomatic: '',
      fastest: '',
      average: '',
      amount: 0,
    },
    active: {
      ideal: null,
      scheduled: null,
      manualIdeal: true,
      manualScheduled: true,
      idealAutomatic: '',
      scheduledAutomatic: '',
      fastest: '',
      average: '',
      amount: 0,
    },
    change: {
      ideal: null,
      scheduled: null,
      manualIdeal: true,
      manualScheduled: true,
      idealAutomatic: '',
      scheduledAutomatic: '',
      fastest: '',
      average: '',
      amount: 0,
    },
  });

  if (loading || (typeof containerWidth === 'number' && containerWidth === 0)) {
    return <Loading background={'transparent'} />;
  }

  const saveData = async (data: StoreType) => {
    if (!ncFileId) return;
    await saveNCFileStatisticsTimes({
      ncFileId,
      clampingDetails: {
        setupTimeSecond: data?.setup.ideal,
        scheduledSetupTimeInSeconds: data?.setup.scheduled,
        useManualIdealSetupTime: data?.setup.manualIdeal,
        useManualScheduledSetupTime: data?.setup.manualScheduled,
        runTimePerPcsSecond: data?.active.ideal,
        scheduledRunTimeInSeconds: data?.active.scheduled,
        useManualIdealRunTime: data?.active.manualIdeal,
        useManualScheduledRunTime: data?.active.manualScheduled,
        changeTimePerPcsSecond: data?.change.ideal,
        scheduledChangeTimeInSeconds: data?.change.scheduled,
        useManualIdealChangeTime: data?.change.manualIdeal,
        useManualScheduledChangeTime: data?.change.manualScheduled,
      },
    });
    Alert({ title: 'Success', type: 'success' });
  };

  const changeStore = (value: string | boolean, level: LevelType, context: ContextType) => {
    let data = store;
    setStore((s) => {
      data = {
        ...s,
        [level]: {
          ...s[level],
          [context]: value,
        },
      };
      return data;
    });

    if (saveTimeOut) {
      clearTimeout(saveTimeOut);
    }

    saveTimeOut = setTimeout(() => {
      saveData(data);
    }, 2000);
  };

  const getBlockData = ({ level, status }: { level: LevelType; status: StatusType }) => ({
    ideal: {
      name: `Ideal ${status}`,
      value: store[level].ideal,
      change: (v: ChangeFunctionValue) => changeStore(v, level, 'ideal'),
    },
    scheduled: {
      name: `Scheduled ${status}`,
      value: store[level].scheduled,
      change: (v: ChangeFunctionValue) => changeStore(v, level, 'scheduled'),
    },
    manualIdeal: {
      value: store[level].manualIdeal,
      change: (v: ChangeFunctionValue) => changeStore(v, level, 'manualIdeal'),
    },
    manualScheduled: {
      value: store[level].manualScheduled,
      change: (v: ChangeFunctionValue) => changeStore(v, level, 'manualScheduled'),
    },
    idealAutomatic: store[level].idealAutomatic,
    scheduledAutomatic: store[level].scheduledAutomatic,
    fastest: store[level].fastest,
    average: store[level].average,
    amount: store[level].amount,
  });

  const BLOCKS = [
    {
      name: 'Per batch',
      status: 'Setup' as StatusType,
      statusColor: 'Status/Light/Setup',
      amountName: 'batches',
      ...getBlockData({ level: 'setup', status: 'Setup' }),
    },
    {
      name: 'Per Pcs',
      status: 'Active' as StatusType,
      statusColor: 'Status/Light/Active',
      amountName: 'pieces',
      ...getBlockData({ level: 'active', status: 'Active' }),
    },
    {
      name: 'Per Pcs',
      status: 'Change' as StatusType,
      statusColor: 'Status/Light/Change',
      amountName: 'pieces',
      ...getBlockData({ level: 'change', status: 'Change' }),
    },
  ];

  const BLOCKS_LENGTH = BLOCKS.length;
  const SPACE_BETWEEN_ITEMS = scale(isPhone ? 15 : 36);
  const AVAILABLE_WIDTH = (containerWidth ?? width) - scale(horizontalOffset ?? 0) - horizontalOffsetForShadow * 2;
  const ELEMENT_WIDTH = isPhone ? AVAILABLE_WIDTH : (AVAILABLE_WIDTH - SPACE_BETWEEN_ITEMS * (BLOCKS_LENGTH - 1)) / BLOCKS_LENGTH;

  const activeBlockConfigForMobile = BLOCKS.find((b) => b.status === activeMobileTabTime);

  return (
    <>
      {isPhone && (
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              borderTopColor: C('Hairline/Light/No trans'),
              borderTopWidth: isPhone ? StyleSheet.hairlineWidth : 0,
              paddingVertical: scale(12),
            },
          ]}
        >
          {BLOCKS.map((block) => block.status).map((status) => (
            <Selectors
              key={status}
              {...{
                scale,
                font,
                selected: status === activeMobileTabTime,
                variant: 'L',
                text: i18n.t(status),
                onPress: () => {
                  trackEvent(TRACKING_EVENTS.TIMES, {
                    action: 'subScreenChanged',
                    perspective: 'ncFile' as const,
                    subScreen: status.toLowerCase() as 'setup' | 'active' | 'change',
                    ncFileId: String(ncFileId),
                  });
                  setActiveMobileTabTime(status);
                },
              }}
            />
          ))}
        </View>
      )}

      <ScrollView>
        <View style={{ width: '100%', alignItems: 'center' }}>
          <View
            style={[
              {
                width: '100%',
                flex: 1,
              },
              !isPhone && { maxWidth: AVAILABLE_WIDTH + scale(horizontalOffsetForShadow * 2) },
            ]}
          >
            {!isPhone && (
              <FlatList
                data={BLOCKS}
                renderItem={({ item, index }) => (
                  <View
                    style={[
                      {
                        width: ELEMENT_WIDTH,
                      },
                    ]}
                  >
                    <Block
                      {...{
                        C,
                        ...item,
                        loading,
                      }}
                    />
                  </View>
                )}
                keyExtractor={(item) => item.status}
                horizontal={true}
                showsHorizontalScrollIndicator={false}
                pagingEnabled={true}
                snapToInterval={ELEMENT_WIDTH + SPACE_BETWEEN_ITEMS}
                decelerationRate={'fast'}
                contentContainerStyle={[{ paddingHorizontal: scale(withoutPaddingHorizontal ? 0 : 15), gap: SPACE_BETWEEN_ITEMS }, style]}
              />
            )}

            {isPhone && activeBlockConfigForMobile && (
              <View
                style={[
                  {
                    width: ELEMENT_WIDTH,
                  },
                ]}
              >
                <Block
                  {...{
                    C,
                    ...activeBlockConfigForMobile,
                    loading,
                  }}
                />
              </View>
            )}
          </View>
          <View
            style={{
              marginTop: scale(20),
              width: '100%',
              maxWidth: AVAILABLE_WIDTH,
              paddingHorizontal: scale(30),
              paddingBottom: scale(50),
            }}
          >
            <View>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Ideal Times are used for')}:</Text>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), paddingLeft: scale(15) }]}>
                <Text style={{ fontWeight: 'bold' }}>
                  {BULLET}
                  {'  '}
                  {i18n.t('Calculating Time Loss:')}{' '}
                </Text>
                {i18n.t('Determine the time lost due to speed reductions (above ideal active), slow setups, or slow changes.')}
              </Text>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), paddingLeft: scale(15) }]}>
                <Text style={{ fontWeight: 'bold' }}>
                  {BULLET}
                  {'  '}
                  {i18n.t('Allocating Paid Time:')}{' '}
                </Text>
                {i18n.t('Distribute the paid time of jobs to linked NC files and batches based on the ratio of their ideal times.')}
              </Text>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(20) }]}>
                {i18n.t('Scheduled Times are used for scheduling.')}
              </Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
};

export default connect(null, { ncFileStatisticsTimes, saveNCFileStatisticsTimes })(ColorWrapper(Times));
